import { useEffect, useState, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import api from '../services/api'
import { PromoPopupCard } from './PromoTemplates'

// Show unseen promos once per browsing session — closing never affects the banner.
const SEEN_KEY = 'tl_promo_popup_seen'
const getSeen = () => {
  try { return new Set(JSON.parse(sessionStorage.getItem(SEEN_KEY) || '[]')) } catch { return new Set() }
}
const markSeen = (ids) => {
  try {
    const s = getSeen(); ids.forEach(id => s.add(id))
    sessionStorage.setItem(SEEN_KEY, JSON.stringify([...s]))
  } catch { /* storage unavailable — just skip */ }
}

export default function PromoPopup() {
  const [promos, setPromos] = useState([])   // the unseen set to show, latest first
  const [idx, setIdx] = useState(0)
  const [open, setOpen] = useState(false)
  const [leaving, setLeaving] = useState(false)

  // Keep a ref of `open` so the poller can avoid interrupting a visible popup.
  const openRef = useRef(false)
  useEffect(() => { openRef.current = open }, [open])

  // Collect all active, undismissed, not-yet-seen promos, newest first.
  // Polls like the notification badge so a newly-pushed promo also triggers the popup,
  // and so it still appears if auth/data weren't ready on first paint.
  useEffect(() => {
    let cancelled = false
    const check = () => {
      api.get('/promotions')
        .then(list => {
          if (cancelled || openRef.current) return   // don't interrupt an open popup
          const arr = Array.isArray(list) ? list : (list?.items ?? [])
          const seen = getSeen()
          const show = arr
            .filter(p => !p.dismissed && !seen.has(p.id))
            .sort((a, b) => (b.id || 0) - (a.id || 0))   // newest first
          if (show.length) {
            setPromos(show)
            setTimeout(() => { if (!cancelled && !openRef.current) setOpen(true) }, 650)
          }
        })
        .catch(() => { /* not logged in / no promos — no popup */ })
    }
    check()                                 // immediate
    const quick = setTimeout(check, 2000)   // quick re-check for slow auth/data on first paint
    const iv = setInterval(check, 60000)    // then poll (like the badge) to catch new promos
    return () => { cancelled = true; clearTimeout(quick); clearInterval(iv) }
  }, [])

  const close = useCallback(() => {
    markSeen(promos.map(p => p.id))
    setLeaving(true)
    setTimeout(() => { setOpen(false); setLeaving(false) }, 220)
  }, [promos])

  const go = useCallback((d) => {
    setIdx(i => (i + d + promos.length) % promos.length)
  }, [promos.length])

  // Scroll lock + keyboard (Esc closes, arrows navigate).
  useEffect(() => {
    if (!open) return
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e) => {
      if (e.key === 'Escape') close()
      else if (e.key === 'ArrowLeft') go(-1)
      else if (e.key === 'ArrowRight') go(1)
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prevOverflow
      window.removeEventListener('keydown', onKey)
    }
  }, [open, close, go])

  // Auto-advance through multiple promos.
  useEffect(() => {
    if (!open || promos.length < 2) return
    const t = setInterval(() => go(1), 6000)
    return () => clearInterval(t)
  }, [open, promos.length, go])

  if (!open || promos.length === 0) return null
  const promo = promos[Math.min(idx, promos.length - 1)]
  const many = promos.length > 1

  const onCTA = () => {
    const link = promo?.cta_link
    close()
    if (link) setTimeout(() => { window.location.href = link }, 230)
  }

  return createPortal(
    <div
      className={`fixed inset-0 z-[200] flex items-center justify-center p-4 transition-opacity duration-200 ${leaving ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-promo-fade" onClick={close} />

      <div className={`relative w-full max-w-md ${leaving ? '' : 'animate-promo-pop'}`}>
        <div className="relative">
          <PromoPopupCard promo={promo} onDismiss={close} onCTA={onCTA} />

          {many && (
            <>
              <button type="button" onClick={() => go(-1)} aria-label="Previous promotion"
                className="absolute left-1.5 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/55 hover:bg-black/75 text-white flex items-center justify-center backdrop-blur border border-white/15 transition-colors">
                <ChevronLeft size={18} />
              </button>
              <button type="button" onClick={() => go(1)} aria-label="Next promotion"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/55 hover:bg-black/75 text-white flex items-center justify-center backdrop-blur border border-white/15 transition-colors">
                <ChevronRight size={18} />
              </button>
            </>
          )}
        </div>

        {many && (
          <div className="flex justify-center gap-1.5 mt-3">
            {promos.map((p, i) => (
              <button key={p.id} type="button" onClick={() => setIdx(i)} aria-label={`Promotion ${i + 1}`}
                className={`h-2 rounded-full transition-all ${i === idx ? 'w-5 bg-white' : 'w-2 bg-white/40 hover:bg-white/70'}`} />
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}
